import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { FormInput } from 'components/molecules/FormInput';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { useTranslations } from 'hooks/useTranslations';
import { Resolver, useForm } from 'react-hook-form';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/atoms/Button';
import { EIconTypes } from 'constants/Icons';
import { removeFileExtension, removeFileNameSpecialCharacters } from 'utils/files';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { Box } from '@mui/system';
import { IDocument } from 'models/legalEntity';
import { useDocumentFormSchema } from './schema';
import { validateAttachment } from '../../../utils/attachments';

const initialDocumentFormValues: IDocument = {
  name: '',
  weaponLicenceDocumentType: '',
  comment: '',
  file: null,
};

interface IDocumentForm {
  setToForm: (arg: any) => void;
}

const DocumentForm = ({ setToForm }: IDocumentForm) => {
  const { t } = useTranslations();
  const [file, setfile] = useState<any>(null);
  const { attachmentDocumentTypeData } = useConfigurationLicence();
  const { schema } = useDocumentFormSchema();
  const { setAlert, clearAlert, documentsAlert } = useAlert();

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_DOCUMENTS_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IDocument>({
    defaultValues: initialDocumentFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IDocument>,
  });

  useEffect(() => {
    if (file?.name) {
      setValue('name', removeFileNameSpecialCharacters(removeFileExtension(file.name)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const onSubmit = async (data: IDocument) => {
    const validation = validateAttachment(file, data.name);

    if (validation.hasError && validation.errorMessage) {
      await setAlert(ActionTypes.SET_DOCUMENTS_ALERT, {
        text: t(validation.errorMessage),
        variant: EAlertVariants.error,
      });
      return;
    }

    if (file) {
      setToForm({ ...data, file });
      reset(initialDocumentFormValues);
      setfile(null);
    }
  };

  return (
    <Grid container columnSpacing="20px" sx={{ rowGap: '10px', mb: 2 }}>
      <Grid size={{ xs: 12, md: 3 }}>
        <FormSelect
          options={mapDictionaryToOptionProp(attachmentDocumentTypeData)}
          setValue={setValue}
          name="weaponLicenceDocumentType"
          label={`${t('legalEntity.licence.documentType.label')}*`}
          control={control}
          errors={errors}
        />
      </Grid>
      <Grid size={{ xs: 8, md: 6 }}>
        <FormInput
          name="name"
          label={`${t('legalEntity.licence.fileName.label')}*`}
          setValue={setValue}
          control={control}
          errors={errors}
        />
      </Grid>
      <Grid size={{ xs: 4, md: 3 }} sx={{ marginTop: { xs: 3.5, md: 0 } }}>
        <Button
          id="legalEntity_licence_browseDocument-button"
          isUploadButton
          alignWithInputSX={{ xs: 12, md: 3 }}
          onClick={(files) => {
            setfile(files[0]);
            clearAlert(ActionTypes.SET_SAME_PAGE_ALERT);
          }}
          label={t('legalEntity.licence.browse.label')}
          size={EButtonSizes.small}
          variant={EButtonVariants.outlined}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, md: 9 }}>
        <FormInput
          name="comment"
          label={t('legalEntity.licence.comment.label')}
          setValue={setValue}
          control={control}
          errors={errors}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Button
          id="legalEntity_licence_addDocument-button"
          variant={EButtonVariants.contained}
          icon={EIconTypes.plus}
          alignWithInputSX={{ xs: 12, md: 3 }}
          label={t('legalEntity.licence.addDocument.label')}
          onClick={handleSubmit(onSubmit)} // submit wewnątrz głównego form
          size={EButtonSizes.small}
          disabled={!file}
          fullWidth
        />
      </Grid>

      {documentsAlert && (
        <Box sx={{ mb: 2 }}>
          <Alert text={documentsAlert.text} variant={documentsAlert.variant} />
        </Box>
      )}
    </Grid>
  );
};

export { DocumentForm };
